import Header from "@/components/header";
import { AddCustomerForm } from "@/models/payload/customer";
import FoundCustomer from "./FoundCustomer";
import {
  Alert,
  Button,
  CloseButton,
  Container,
  Grid,
  Group,
  InputBase,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Title
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";


import useAddCustomer from "../hooks/useAddCustomer";
import useGetCustomerByPhoneNumber, { useGetCustomerInMerchantByPhoneNumber } from "../hooks/useGetCustomerByPhoneNumber";

import BirthDateSelector from "@/components/birthDateSelector";
import { useAuth } from "@/context/auth";
import useIsSmall from "@/hooks/useIsSmall";
import { Gender, MerchantCustomerNotifierType, MerchantUserType } from "@/models/club";
import { useGetCheckNotifier } from "@/services/autoMessage/hooks/useCheckNotifierBalance";
import { useGetMerchantForMerchantUser } from "@/services/merchant/utilities";
import PersianUtil from "@/services/persianUtiles/numberPersianUtiles";
import moment from "moment-jalaali";

import { schema } from "./type";

type Props = {
  close?: (customerId: number) => void;
};

function AddCustomer({ close }: Props) {
  const { getUserByToken } = useAuth();
  const { data: merchantUser } = useGetMerchantForMerchantUser(
    getUserByToken()?.phoneNumber ?? ""
  );

  const isAdmin = merchantUser?.merchantUserType !== MerchantUserType.Value2;

  const { addCustomer, isLoading } = useAddCustomer({
    doNavigate: true,
    close,
    route: isAdmin ? "/dashboard/customers" : "/dashboard",
  });

  const { data: checkNotification } = useGetCheckNotifier(
    MerchantCustomerNotifierType.Value0
  );
  const form = useForm<AddCustomerForm>({
    initialValues: {
      phoneNumber: "",
      firstName: "",
      lastName: "",
      referrer: "",
      birthDate: "",
      dateOfMarriage: "",
      year: "",
      month: "",
      day: "",
      gender: null,
      // purchaseAmount: undefined,
    },
    validate: yupResolver(schema),
  });

  const filter = form.values.phoneNumber || "";
  const validPhone = /^09\d{9}$/.test(filter);
  const lookup = useGetCustomerByPhoneNumber({ phoneNumber: filter });
  const { data, isLoading: isLoadingSearch, isSuccess } = lookup;
  const salonLookup = useGetCustomerInMerchantByPhoneNumber({
    phoneNumber: filter,
    enabled: validPhone && isSuccess && !data,
  });
  const dataInMerchant = salonLookup.data;
  const isLoadingSearchInMerchant = salonLookup.isFetching;
  const foundCustomer = validPhone ? data || dataInMerchant : undefined;
  const showCustomer = Boolean(foundCustomer);
  const searchFailed = lookup.isError || (!data && salonLookup.isError);
  const searchComplete = validPhone && isSuccess && (Boolean(data) || salonLookup.isSuccess);
  const matches = useIsSmall();
  const submit = (values: AddCustomerForm) => {
    if (!searchComplete || foundCustomer || searchFailed) return;
    addCustomer({
      ...values,
      referrer: values.referrer?.trim() || null,
      birthDate: (() => {
        const y = values?.year?.toString() || "";
        const m = values?.month?.toString() || "";
        const d = values?.day?.toString() || "";
        if (!y || !m || !d) return undefined;
        const jalali = `${y}/${m}/${d}`;
        const parsed = moment(jalali, "jYYYY/jMM/jDD", true);
        return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
      })(),
      gender: !values?.gender
        ? undefined
        : values?.gender === "0"
          ? Gender.Value0
          : Gender.Value1,
    });
  };
  return (
    <Stack h={matches ? "100svh" : ""} gap="0">
      {close ? (
        <Group justify="flex-end" px="lg" py="xs">
          <CloseButton onClick={() => close(0)} />
        </Group>
      ) : (
        <Header title="افزودن مشتری جدید" showBack />
      )}

      <Container w="100%" size="md" h={matches ? "100svh" : ""}>
        <Title order={4}>افزودن مشتری جدید</Title>
        <Stack mt="lg">
          {!checkNotification?.isEnoughBalance && isSuccess && (
            <Alert variant="light" color="yellow">
              <Text size="sm">
                با توجه به فعال بودن ارسال پیامک خودکار بعد از ثبت مشتری ،
                موجودی حساب شما برای ارسال پیامک کافی نمی باشد. لطفا نسبت به
                شارژ حساب خود اقدام فرمایید.
              </Text>
            </Alert>
          )}

          <form
            onSubmit={form.onSubmit(submit)}
            name="add-customer-form"
            className="w-100"
            id="add-customer-form"
          >
            <Stack>
              <InputBase
                size="lg"
                type="tel"
                dir="ltr"
                autoFocus
                styles={{
                  input: {
                    textAlign: "left",
                    paddingLeft: "16px",
                  },
                  label: {
                    fontSize: "1rem",
                  },
                }}
                value={form.values.phoneNumber || ""}
                maxLength={11}
                label="شماره تلفن مشتری را وارد نمایید"
                leftSection={
                  isLoadingSearch && <Loader size="xs" type="dots" />
                }
                withAsterisk
                onChange={(e) => {
                  form.setValues({
                    phoneNumber: PersianUtil.currentNumberToEnglish(
                      e.target.value
                    ),
                  });
                }}
                error={form.errors.phoneNumber}
              />

              {searchFailed && <Alert color="red">جست‌وجوی مشتری انجام نشد. <Button type="button" variant="subtle" onClick={() => { if (lookup.isError) void lookup.refetch(); else void salonLookup.refetch(); }}>تلاش دوباره</Button></Alert>}
              {!showCustomer ? (
                <>
                  <Grid>
                    <Grid.Col span={6}>
                      <TextInput
                        size="lg"
                        styles={{
                          label: {
                            fontSize: "1rem",
                          },
                        }}

                        label="نام"
                        {...form.getInputProps("firstName")}
                        disabled={isLoadingSearch || isLoadingSearchInMerchant || !!dataInMerchant}
                      />
                    </Grid.Col>

                    <Grid.Col span={6}>
                      <TextInput
                        size="lg"
                        styles={{
                          label: {
                            fontSize: "1rem",
                          },
                        }}
                        placeholder=""
                        label="نام خانوادگی"
                        {...form.getInputProps("lastName")}
                        disabled={isLoadingSearch || isLoadingSearchInMerchant || !!dataInMerchant}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Select
                        size="lg"
                        placeholder=""
                        styles={{
                          label: {
                            fontSize: "1rem",
                          },
                        }}
                        label="جنسیت"
                        {...form.getInputProps("gender")}
                        disabled={isLoadingSearch || isLoadingSearchInMerchant || !!dataInMerchant}
                        data={[
                          {
                            value: Gender.Value1.toString(),
                            label: "خانم",
                          },
                          {
                            value: Gender.Value0.toString(),
                            label: "مرد",
                          },
                        ]}
                      ></Select>
                    </Grid.Col>

                    <Grid.Col span={12}>
                      <TextInput label="معرف (اختیاری)" size="lg" maxLength={200}
                        {...form.getInputProps("referrer")} disabled={isLoading} />
                    </Grid.Col>
                    <BirthDateSelector
                      day={form.values.day ?? undefined}
                      month={form.values.month ?? undefined}
                      year={form.values.year ?? undefined}
                      onDayChange={(value) =>
                        form.setFieldValue("day", value ?? "")
                      }
                      onMonthChange={(value) =>
                        form.setFieldValue("month", value ?? "")
                      }
                      onYearChange={(value) =>
                        form.setFieldValue("year", value ?? "")
                      }
                      disabled={isLoadingSearch || isLoadingSearchInMerchant || !!dataInMerchant}
                      dayError={
                        typeof form.errors.day === "string"
                          ? form.errors.day
                          : undefined
                      }
                      monthError={
                        typeof form.errors.month === "string"
                          ? form.errors.month
                          : undefined
                      }
                      yearError={
                        typeof form.errors.year === "string"
                          ? form.errors.year
                          : undefined
                      }
                      size="lg"
                    />
                    <Grid.Col span={12}>
                      {form.errors["all-or-none-date"] && (
                        <Text>تاریخ تولد را وارد نمایید</Text>
                      )}
                    </Grid.Col>
                  </Grid>

                  {/* {checked ? (
                    <Stack gap="xs">
                      <PersianSupportNumberInput
                        withThousandsSeparator
                        styles={{
                          input: {
                            direction: "ltr",
                            textAlign: "left",
                          },
                        }}
                        label="مبلغ خرید (ریال)"
                        onChangeHandler={(e) =>
                          form.setValues({
                            purchaseAmount: Number(e),
                          })
                        }
                        size="md"
                      />
                      {form?.values?.purchaseAmount ? (
                        <Group gap={5}>
                          <Text size="xs" c="blue">
                            {Num2persian(
                              form?.values?.purchaseAmount?.toString() || "0"
                            )}
                          </Text>
                          <Text size="xs" c="blue">
                            ریال
                          </Text>
                        </Group>
                      ) : (
                        ""
                      )}

                      <Textarea
                        size="md"
                        placeholder=""
                        label="توضیحات خرید"
                        {...form.getInputProps("description")}
                        disabled={isLoadingSearch}
                      />
                    </Stack>
                  ) : null} */}
                </>
              ) : (
                <>
                  {foundCustomer && <FoundCustomer key={`${filter}-${foundCustomer.id}`} customer={foundCustomer} phoneNumber={filter} close={close} />}
                </>
              )}

            </Stack>
          </form>
        </Stack>
      </Container>

      <Container w="100%" size="md" h={matches ? "100svh" : ""}>
        <Stack>
          {!showCustomer ? (
            <Button
              type="submit"
              size="md"
              my="md"
              fullWidth
              form="add-customer-form"

              loading={isLoading || isLoadingSearch || isLoadingSearchInMerchant}
              disabled={!searchComplete || searchFailed}
            >
              ثبت
            </Button>
          ) : null}
        </Stack>
      </Container>
    </Stack>
  );
}

export default AddCustomer;
